import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Badge,
  Flex,
  useDisclosure,
  useToast,
  useColorModeValue,
  Text,
  Box,
  Portal,
  IconButton,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { useCreateProductMutation } from "../../productApi/productAPI";
import { Product } from "../../types/product";
import { storage } from "../../configs/firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import {
  LayoutGrid,
  MenuSquareIcon,
  SearchIcon,
  User2,
  Bell,
  X,
} from "lucide-react";

import { useAppSelector } from "../../store/hooks/hooks";
import { NavItem } from "../SideBar/SideBar";
import GlobalSearch from "../Search/GlobalSearch";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { scrollPageToTop } from "../../utils/scrollPageToTop";

const NOTIFICATIONS_API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/notifications`;

const cardsBorderRadii = "8px";

const hexToRgba = (hex: string, alpha: number) => {
  const cleaned = hex.replace("#", "");

  if (cleaned.length !== 6) {
    return `rgba(100,116,139,${alpha})`;
  }

  const bigint = parseInt(cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const MobileFooterNavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [createProduct] = useCreateProductMutation();
  const toast = useToast();
  const { theme } = useCustomTheme();

  const isDark = theme.name === "dark";
  const footerBorderBottom = isDark
    ? "1px solid rgba(255, 255, 255, 0.08)"
    : "1px solid #E2E8F0";

  const mobileLightBackground = {
    base: isDark ? theme.surface : "#F8FAFC",
    md: theme.surface,
  };

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const hoverBg = useColorModeValue("gray.100", "gray.700");

  const [cartOpen, setCartOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const cart = useAppSelector((state) => state.cart);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isCustomModalOpen, setCustomModalOpen] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  const [newProduct, setNewProduct] = useState<Product>({
    title: "",
    price: 0,
    imageUrl: "",
    description: "",
    category: "",
    userId: user?.uid,
  });

  const fetchUnreadNotificationCount = async () => {
    if (!user?.uid) {
      setUnreadNotificationCount(0);
      return;
    }

    try {
      const res = await axios.get(`${NOTIFICATIONS_API}/unread-count`, {
        params: { userId: user.uid },
      });

      setUnreadNotificationCount(Number(res.data?.count || 0));
    } catch (error) {
      console.error("Failed to fetch unread notifications:", error);
      setUnreadNotificationCount(0);
    }
  };

  useEffect(() => {
    fetchUnreadNotificationCount();

    const interval = window.setInterval(() => {
      fetchUnreadNotificationCount();
    }, 30000);

    return () => window.clearInterval(interval);
  }, [user?.uid]);

  const mobileFooteNavItems: NavItem[] = [
    {
      id: 1,
      name: "Home",
      icon: MenuSquareIcon,
      path: "/dashboard",
      color: "#2563EB",
    },
    {
      id: 2,
      name: "Explore",
      icon: LayoutGrid,
      path: "/explore",
      color: "#14B8A6",
    },
    {
      id: 3,
      name: "Inbox",
      icon: Bell,
      path: "/notifications",
      color: "#7C3AED",
    },
    {
      id: 4,
      name: "Profile",
      icon: User2,
      path: "/profile",
      color: "#F59E0B",
    },
  ];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setNewProduct({ ...newProduct, [e.target.name]: e.target.value });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const storageRef = ref(storage, `products/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    setUploading(true);
    setUploadProgress(0);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => {
        console.error("Upload failed:", error);
        toast({
          title: "Upload failed",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setUploading(false);
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        setNewProduct((prev) => ({ ...prev, imageUrl: downloadURL }));
        setUploading(false);

        toast({
          title: "Image uploaded",
          description: "Image successfully uploaded.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      },
    );
  };

  const handleCreate = async () => {
    if (!newProduct.title || !newProduct.imageUrl) {
      toast({
        title: "Missing data",
        description: "Please fill in all required fields and upload an image.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    try {
      await createProduct(newProduct);

      toast({
        title: "Product created",
        description: `${newProduct.title} was successfully added.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setNewProduct({
        title: "",
        price: 0,
        imageUrl: "",
        description: "",
        category: "",
        userId: user?.uid,
      });

      setUploadProgress(0);
      onClose();
    } catch (error) {
      toast({
        title: "Product creation failed",
        description: (error as Error).message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Flex
        justifyContent={"space-between"}
        pl={"16px"}
        pr={"22px"}
        height="100%"
        minH="60px"
        pt="3px"
        pb="env(safe-area-inset-bottom)"
        boxSizing="border-box"
        borderTop={`1px solid ${theme.border}`}
        borderBottom={footerBorderBottom}
        alignItems={"center"}
        boxShadow={{
          base: isDark ? theme.shadowSm : "0 -3px 10px rgba(15, 23, 42, 0.08)",
          md: theme.shadowSm,
        }}
        bg={mobileLightBackground}
        color={theme.textMuted}
        sx={{
          WebkitTransform: "translateZ(0)",
          transform: "translateZ(0)",
        }}
      >
        {mobileFooteNavItems.map((item) => {
          const targetPath =
            item.name === "Profile"
              ? user
                ? "/profile"
                : "/login"
              : item.path;

          const isActive = location.pathname.startsWith(targetPath);
          const isNotifications = item.name === "Inbox";
          const cardsBorderRadii = "8px";

          const unreadLabel =
            unreadNotificationCount > 99
              ? "99+"
              : String(unreadNotificationCount);

          return (
            <Flex
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              p={"4px"}
              key={item.id}
              onClick={() => {
                navigate(targetPath);
                scrollPageToTop();
              }}
              borderRadius="4px"
              bg={{
                base: !isDark
                  ? "#F8FAFC"
                  : isActive
                    ? hexToRgba(item.color, 0.18)
                    : theme.surface,
                md: isActive
                  ? hexToRgba(item.color, isDark ? 0.18 : 0.12)
                  : theme.surface,
              }}
              border={
                isActive
                  ? `1px solid ${hexToRgba(item.color, isDark ? 0.34 : 0.24)}`
                  : "1px solid transparent"
              }
              boxShadow={isActive ? theme.shadowSm : "none"}
              _hover={{
                boxShadow: theme.shadowSm,
                cursor: "pointer",
                bg: {
                  base: !isDark
                    ? "#F8FAFC"
                    : isActive
                      ? hexToRgba(item.color, 0.18)
                      : theme.hover,
                  md: isActive
                    ? hexToRgba(item.color, isDark ? 0.18 : 0.12)
                    : theme.hover,
                },
              }}
            >
              <Box position="relative" display="inline-flex" overflow="visible">
                <item.icon
                  fill={isActive ? hexToRgba(item.color, 0.14) : "transparent"}
                  color={item.color}
                  width="22px"
                  height="22px"
                />

                {isNotifications && unreadNotificationCount > 0 && (
                  <Badge
                    position="absolute"
                    top="-6px"
                    right="-9px"
                    minW="18px"
                    h="18px"
                    px="4px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius={cardsBorderRadii}
                    bg="red.500"
                    color="white"
                    fontSize="0.6rem"
                    fontWeight="700"
                    lineHeight="1"
                    zIndex={2}
                    boxShadow="sm"
                  >
                    {unreadLabel}
                  </Badge>
                )}
              </Box>

              <Text
                fontSize={{ base: "xs" }}
                color={isActive ? theme.text : theme.textMuted}
                fontWeight={isActive ? "700" : "500"}
                lineHeight="1.1"
                mt="2px"
                userSelect="none"
                pointerEvents="none"
                sx={{
                  WebkitUserSelect: "none",
                  caretColor: "transparent",
                }}
              >
                {item.name}
              </Text>
            </Flex>
          );
        })}

        <Flex
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          onClick={() => setCustomModalOpen(true)}
          borderRadius={cardsBorderRadii}
          bg={mobileLightBackground}
          p="4px"
          _hover={{
            boxShadow: theme.shadowSm,
            cursor: "pointer",
            bg: {
              base: isDark ? theme.hover : "#F8FAFC",
              md: theme.hover,
            },
          }}
        >
          <SearchIcon
            fill="transparent"
            color="#06B6D4"
            width="22px"
            height="22px"
          />

          <Text
            fontSize={{ base: "xs" }}
            color={theme.textMuted}
            fontWeight="500"
            lineHeight="1.1"
            mt="2px"
            userSelect="none"
            pointerEvents="none"
            sx={{
              WebkitUserSelect: "none",
              caretColor: "transparent",
            }}
          >
            Search
          </Text>
        </Flex>
      </Flex>

      {isCustomModalOpen && (
        <Portal>
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={3000}
            display="flex"
            alignItems="flex-start"
            justifyContent="center"
            px={{ base: 3, sm: 4 }}
            pt={{
              base: "calc(env(safe-area-inset-top) + 12px)",
              md: 6,
            }}
            pb={{
              base: "calc(env(safe-area-inset-bottom) + 84px)",
              md: 6,
            }}
            bg="rgba(0, 0, 0, 0.28)"
            backdropFilter="blur(4px)"
            onClick={() => setCustomModalOpen(false)}
            sx={{
              WebkitTransform: "translateZ(0)",
              transform: "translateZ(0)",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Box
              width="100%"
              maxW="720px"
              maxH={{
                base: "calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 100px)",
                md: "calc(100vh - 48px)",
              }}
              overflowY="auto"
              overflowX="visible"
              bg={theme.surface}
              border={`1px solid ${theme.border}`}
              borderRadius={cardsBorderRadii}
              boxShadow={theme.shadowMd}
              p={{ base: 3, md: 4 }}
              onClick={(event) => event.stopPropagation()}
              sx={{
                WebkitOverflowScrolling: "touch",
                overscrollBehavior: "contain",
                "& *": {
                  maxHeight: "none",
                },
                "& [role='listbox'], & [data-search-results], & .search-results, & .global-search-results":
                  {
                    maxHeight: "none !important",
                    overflow: "visible !important",
                  },
              }}
            >
              <Flex
                align="center"
                justify="space-between"
                gap={3}
                mb={3}
                pb={3}
                borderBottom="1px solid"
                borderColor={theme.border}
              >
                <Box minW={0}>
                  <Text
                    fontSize="sm"
                    fontWeight="800"
                    color={theme.text}
                    noOfLines={1}
                  >
                    Search PickDengu
                  </Text>

                  <Text fontSize="xs" color={theme.textSubtle} noOfLines={1}>
                    Products, services, bookings, orders, and businesses
                  </Text>
                </Box>

                <IconButton
                  aria-label="Close search"
                  icon={<X size={18} />}
                  size="sm"
                  borderRadius={cardsBorderRadii}
                  variant="ghost"
                  color={theme.text}
                  bg={isDark ? "rgba(255, 255, 255, 0.06)" : "gray.100"}
                  border="1px solid"
                  borderColor={theme.border}
                  flexShrink={0}
                  onClick={() => setCustomModalOpen(false)}
                  _hover={{
                    bg: theme.hover,
                  }}
                  _active={{
                    bg: theme.pressed || theme.hover,
                  }}
                />
              </Flex>

              <GlobalSearch
                onResultSelect={() => setCustomModalOpen(false)}
                inlineResults
              />
            </Box>
          </Box>
        </Portal>
      )}
    </>
  );
};

export default MobileFooterNavBar;
